import * as ModelEvent from "@node-elion/syncron";
import { compareDesc } from "date-fns";
import { compact } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import { createLogger, positionBasedSortHelper } from "../../utils";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import {
	NonEditableProperties,
	NonEditablePropertyNames,
} from "../../types/NonEditableProperties";
import { ModelId } from "../../types/ModelId";
import Main from "../../pages/Settings/pages/Tariffs/tabs/Auto/components/Modal/components/Content/tabs/Main";
import Rules from "../../pages/Settings/pages/Tariffs/tabs/Auto/components/Modal/components/Content/tabs/Main/components/Rules";
import { Units } from "../../pages/Settings/pages/Tariffs/constants/constants";
import { TaxiServiceOld as TaxiService, Card, Base, Language } from "..";

import { ruleFromResponse, ruleToRequest } from "./utils";

const logger = createLogger({ name: "AutoTariff" });

class AutoTariff extends Base {
	private static _Card: Card | null = null;

	public static get Card() {
		if (this._Card) return this._Card;
		this._Card = new Card((prpc) => prpc.theirsModel.autoRate.card);
		return this._Card;
	}

	public static fromResponse(data: any): AutoTariff.Model {
		const taxiServices = data.autoRateToTaxiServices.flatMap(
			(item) => item.taxiService,
		);

		const { units } = data.additionalFields.general.hourlyServiceInterval;
		const externalValue =
			data.additionalFields.general.hourlyServiceInterval.value;

		const value =
			units === "hours"
				? externalValue / 1000 / 3600
				: externalValue / 1000 / 60;

		const rules: any[] = data.rules.map(ruleFromResponse);
		const sortRules = rules.sort((from, to) => {
			const sort = compareDesc(
				new Date(to.position ?? 0),
				new Date(from.position ?? 0),
			);
			return sort;
		});

		return {
			id: data.id,

			name: data.name,
			active: data.active,
			disableOthers: data.disableOthers,
			additionalFields: {
				...data.additionalFields,
				general: {
					orderCostNotLessThan: { active: false, value: 0 },
					...data.additionalFields.general,
					hourlyServiceInterval: {
						...data.additionalFields.general.hourlyServiceInterval,
						value,
					},
				},
			},
			rules: sortRules,
			taxiServices,
			taxiServiceIds: taxiServices.map(({ id }) => id),
			position: positionBasedSortHelper.normalizePosition(data?.position),

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};
	}

	public static toRequest(model: AutoTariff.New | AutoTariff.Modified) {
		const general = model.additionalFields?.general;
		const units = general?.hourlyServiceInterval?.units;
		const externalValue = general?.hourlyServiceInterval.value ?? 0;
		const value =
			units === Units.HOURS
				? externalValue * 1000 * 3600
				: externalValue * 1000 * 60;

		const rules = model.rules?.map(ruleToRequest);
		logger.info("[AutoTariff] rules", { rules, model });

		return {
			name: model.name,
			active: model.active,
			disableOthers: model.disableOthers,
			additionalFields: model.additionalFields
				? {
						...model.additionalFields,
						general: {
							orderCostNotLessThan: { active: false, value: 0 },
							...general,
							hourlyServiceInterval: {
								...general?.hourlyServiceInterval,
								value,
							},
						},
				  }
				: undefined,
			rules,
			taxiServiceIds: model.taxiServiceIds,
		};
	}

	public static async getAll() {
		const res = await this.request((prpc) =>
			prpc.theirsModel.autoRate.getAll(),
		);
		return res?.items?.map(this.fromResponse);
	}

	public static async updatePosition(object: {
		id: ModelId;
		position: number;
	}) {
		try {
			if (!object.id) return null;
			if (!object.position) return null;

			const res = await this.request((prpc) =>
				prpc.theirsModel.autoRate.rearrange(object.id, object.position),
			);

			logger.info("[AutoTariff] updatePosition", {
				id: object.id,
				position: object.position,
				res,
			});
			return res;
		} catch (err: any) {
			logger.error("[AutoTariff] Error in updatePosition:", err);
			return null;
		}
	}

	// public static async rearrange(id: ModelId, position: number) {
	//   try {

	// 		logger.info("[AutoTariff] rearrange", { id, position });
	// 		await this.request((prpc) =>
	// 			prpc.theirsModel.autoRate.rearrange(id, position),
	// 		);
	// 	} catch (error: any) {
	// 		logger.error("Error in rearrange method:", error);
	// 	}
	// }

	public static async copy(id: ModelId) {
		try {
			await this.request((prpc) => prpc.theirsModel.autoRate.copy(id));
		} catch (err: any) {
			throw new Error("Error in copy method:", err);
		}
	}

	public static async store(object: AutoTariff.New, force = false) {
		try {
			logger.info("[AutoTariff] store", object);
			await this.request((prpc) =>
				prpc.theirsModel.autoRate.create(this.toRequest(object), force),
			);
		} catch (err: any) {
			if (err.status === 400 && err.message.includes("force")) {
				return false;
			}
		}
		return true;
	}

	public static async update(object: AutoTariff.Modified, force = false) {
		try {
			logger.info("[AutoTariff] update", object);
			await this.request((prpc) =>
				prpc.theirsModel.autoRate.update(
					object.id,
					this.toRequest(object),
					force,
				),
			);
		} catch (err: any) {
			if (err.status === 400 && err.message.includes("force")) {
				return false;
			}
		}
		return true;
	}

	public static async delete(id: number[] | number) {
		this.request((prpc) => prpc.theirsModel.autoRate.delete(id));
	}

	public static async subscribe(
		options: AutoTariff.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<AutoTariff.Model>,
	): Promise<Subscription<AutoTariff.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				const items = compact(state?.models || []);

				const modelsTableSort = items.map(this.fromResponse);

				const models = positionBasedSortHelper(modelsTableSort); //* use only sort position !!! This rule business

				onUpdate({ ...state, models });

				logger.info("[AutoTariff] subscribe", options, models);
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.autoRate.subscribe({
					params: options,
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "AutoTariff.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: AutoTariff.SubscribeOptions) =>
				subscription.update(options),
		} as Subscription<AutoTariff.SubscribeOptions>;
	}
}

declare namespace AutoTariff {
	interface Model extends NonEditableProperties {
		name: string;
		active: boolean;
		disableOthers: boolean;
		additionalFields: Main.AdditionalFields;
		taxiServices: TaxiService.Model[];
		taxiServiceIds: number[];
		rules: Rules.Rule[];
		position: number;
	}

	type New = Omit<Model, NonEditablePropertyNames>;
	type Modified = Partial<New> &
		Partial<Omit<NonEditableProperties, "id">> & { readonly id: number };

	interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<AutoTariff.Model> {
		active?: boolean;
		language?: Language;
		taxiServiceIds?: number[];
	}
}

export default AutoTariff;
